import { computed, ref, unref, watch } from "@vue/composition-api";
import { cloneDeep, defaultsDeep, get, isPlainObject, trimEnd } from "lodash";

import { useFetchEx } from "./fetch";
import { useUserState } from "./user";

/**
 * @typedef {import("@vueuse/core").MaybeRef} MaybeRef
 * @typedef {import("@vue/composition-api").Ref} Ref
 * @typedef {import("./fetch").UseFetchExParams} UseFetchExParams
 */

export function useMock(mockData, options = { immediate: true }) {
  const loading = ref(true);
  const finished = ref(false);
  const data = ref(null);

  const execute = () => {
    loading.value = true;
    setTimeout(() => {
      data.value = cloneDeep(mockData);
      loading.value = false;
      finished.value = true;
    }, 500);
  };

  if (options.immediate) {
    execute();
  }

  return {
    data,
    loading,
    finished,
    execute
  };
}

/**
 * @param {import("@vueuse/shared").MaybeRef<string>} url
 * @param {UseFetchExParams} params
 */
export function useApi(path, { reqInit, ...options } = { reqInit: {} }) {
  const baseUrl = trimEnd(process.env.VUE_APP_API_URL, "/");

  const url = computed(() => {
    const pathValue = unref(path);
    return `${baseUrl}${pathValue}`;
  });

  const { token, locale } = useUserState();

  const localeDict = {
    fr: "fr_FR",
    ar: "ar_001",
    en: "en_US"
  };

  const headers = {
    "Request-Type": "json"
  };

  watch(
    locale,
    () => {
      headers["Accept-Language"] = localeDict[locale.value];
    },
    { immediate: true }
  );

  watch(
    token,
    () => {
      headers["Access-Token"] = token.value;
    },
    { immediate: true }
  );

  const q = useFetchEx(url, {
    ...options,
    reqInit: {
      ...reqInit,
      headers
    }
  });

  const data = computed(() => {
    const result = get(q.data.value, "result", options.defaultValue);

    if (isPlainObject(result))
      return defaultsDeep(result, options.defaultValue);

    return result;
  });

  const error = computed(() => {
    let err = get(q.data.value, "error.data", null);

    if (!err) err = get(q.data.value, "error", null);

    if (!err) err = q.error.value;

    return err;
  });

  const success = computed(() => {
    return q.success.value && !error.value;
  });

  return {
    ...q,
    data,
    error,
    success
  };
}
